import { FC, useState, FormEvent, ChangeEvent } from 'react';
import { Helmet } from 'react-helmet';
import { useTranslation } from 'react-i18next';
import { localBusinessSchema, breadcrumbSchema } from '../../utils/schemas';
import { pushGtmEvent, gtmEvents } from '../../utils/gtm';
import Turnstile from '../../components/Turnstile/Turnstile';

const TURNSTILE_SITE_KEY = process.env.REACT_APP_TURNSTILE_SITE_KEY || '';

interface FormData {
  name: string;
  email: string;
  phone: string;
  company: string;
  subject: string;
  message: string;
  website: string; // honeypot - left blank by real users
  acceptTerms: boolean;
  acceptPrivacy: boolean;
}

interface FormStatus {
  submitting: boolean;
  submitted: boolean;
  success: boolean;
  message: string;
}

const Contact: FC = () => {
  const { t } = useTranslation();

  const [formData, setFormData] = useState<FormData>({
    name: '',
    email: '',
    phone: '',
    company: '',
    subject: '',
    message: '',
    website: '',
    acceptTerms: false,
    acceptPrivacy: false
  });

  const [status, setStatus] = useState<FormStatus>({
    submitting: false,
    submitted: false,
    success: false,
    message: ''
  });

  const [turnstileToken, setTurnstileToken] = useState('');

  const handleChange = (e: ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value, type } = e.target;
    const newValue = type === 'checkbox' ? (e.target as HTMLInputElement).checked : value;
    setFormData(prev => ({ ...prev, [name]: newValue }));
  };

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setStatus({ submitting: true, submitted: false, success: false, message: '' });

    try {
      const apiBaseUrl = process.env.REACT_APP_API_BASE_URL || '/api';
      const response = await fetch(`${apiBaseUrl}/forms/contact/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ ...formData, turnstile_token: turnstileToken }),
      });

      const data = await response.json();

      if (response.ok && data.success) {
        pushGtmEvent(gtmEvents.contactFormSubmission, {
          form_name: 'contact',
          subject: formData.subject,
        });
        setStatus({
          submitting: false,
          submitted: true,
          success: true,
          message: t('contact.form.successMessage') || data.message
        });
        // Reset form
        setFormData({
          name: '',
          email: '',
          phone: '',
          company: '',
          subject: '',
          message: '',
          website: '',
          acceptTerms: false,
          acceptPrivacy: false
        });
        setTurnstileToken('');
      } else {
        setStatus({
          submitting: false,
          submitted: true,
          success: false,
          message: data.message || t('contact.form.errorMessage') || 'Failed to send message. Please try again.'
        });
      }
    } catch {
      setStatus({
        submitting: false,
        submitted: true,
        success: false,
        message: t('contact.form.errorMessage') || 'Failed to send message. Please try again or contact us directly.'
      });
    }
  };

  return (
    <div className="contact-page">
      <Helmet>
        <title>{t('contact.meta.title')}</title>
        <meta name="description" content={t('contact.meta.description')} />
        <meta name="keywords" content={t('contact.meta.keywords')} />
        <script type="application/ld+json">
          {JSON.stringify(localBusinessSchema())}
        </script>
        <script type="application/ld+json">
          {JSON.stringify(breadcrumbSchema([
            { name: 'Home', path: '/' },
            { name: 'Contact', path: '/contact' },
          ]))}
        </script>
      </Helmet>

      {/* Hero Section */}
      <section className="contact-hero">
        <div className="container">
          <h1>{t('contact.hero.title')}</h1>
          <p className="contact-subtitle">
            {t('contact.hero.subtitle')}
          </p>
        </div>
      </section>

      {/* Contact Content */}
      <section className="section section-white">
        <div className="container">
          <div className="contact-content">
            <div className="contact-info">
              <h2>{t('contact.info.title')}</h2>
              <p className="contact-description">
                {t('contact.info.description')}
              </p>

              <div className="contact-details">
                <div className="contact-detail-item">
                  <div className="contact-icon">
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/>
                      <circle cx="12" cy="10" r="3"/>
                    </svg>
                  </div>
                  <div>
                    <h3>{t('contact.info.address.title')}</h3>
                    <p>{t('contact.info.address.line1')}</p>
                  </div>
                </div>

                <div className="contact-detail-item">
                  <div className="contact-icon">
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/>
                      <polyline points="22,6 12,13 2,6"/>
                    </svg>
                  </div>
                  <div>
                    <h3>{t('contact.info.email.title')}</h3>
                    <p><a href={`mailto:${t('contact.info.email.address')}`} onClick={() => pushGtmEvent(gtmEvents.emailClick, { location: 'contact_page', email: t('contact.info.email.address') })}>{t('contact.info.email.address')}</a></p>
                  </div>
                </div>

                <div className="contact-detail-item">
                  <div className="contact-icon">
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"/>
                    </svg>
                  </div>
                  <div>
                    <h3>{t('contact.info.phone.title')}</h3>
                    <p><a href="tel:+355672063632" onClick={() => pushGtmEvent(gtmEvents.phoneClick, { location: 'contact_page', phone: '+355672063632' })}>{t('contact.info.phone.number')}</a></p>
                  </div>
                </div>

                <div className="contact-detail-item">
                  <div className="contact-icon">
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <circle cx="12" cy="12" r="10"/>
                      <polyline points="12,6 12,12 16,14"/>
                    </svg>
                  </div>
                  <div>
                    <h3>{t('contact.info.hours.title')}</h3>
                    <p>{t('contact.info.hours.weekdays')}<br/>{t('contact.info.hours.weekend')}</p>
                  </div>
                </div>

                <div className="contact-detail-item">
                  <div className="contact-icon">
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M16 8a6 6 0 0 1 6 6v7h-4v-7a2 2 0 0 0-4 0v7h-4v-7a6 6 0 0 1 6-6z"/>
                      <rect x="2" y="9" width="4" height="12"/>
                      <circle cx="4" cy="4" r="2"/>
                    </svg>
                  </div>
                  <div>
                    <h3>{t('contact.info.social.title')}</h3>
                    <div className="contact-social-links">
                      <a href="https://www.instagram.com/msc_certifications" target="_blank" rel="noopener noreferrer">
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                          <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z"/>
                        </svg>
                        Instagram
                      </a>
                      <a href="https://www.linkedin.com/company/msc-certifications/" target="_blank" rel="noopener noreferrer">
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                          <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 01-2.063-2.065 2.064 2.064 0 112.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
                        </svg>
                        LinkedIn
                      </a>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="contact-form-section">
              <h2>{t('contact.form.title')}</h2>

              {status.submitted && (
                <div className={`form-message ${status.success ? 'form-message-success' : 'form-message-error'}`}>
                  {status.message}
                </div>
              )}

              <form className="contact-form" onSubmit={handleSubmit}>
                <div className="hp-field" aria-hidden="true">
                  <label htmlFor="website">Website</label>
                  <input
                    type="text"
                    id="website"
                    name="website"
                    value={formData.website}
                    onChange={handleChange}
                    tabIndex={-1}
                    autoComplete="off"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="name">{t('contact.form.name')} *</label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    placeholder={t('contact.form.namePlaceholder')}
                    required
                    disabled={status.submitting}
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="email">{t('contact.form.email')} *</label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    placeholder={t('contact.form.emailPlaceholder')}
                    required
                    disabled={status.submitting}
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="phone">{t('contact.form.phone')}</label>
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    placeholder={t('contact.form.phonePlaceholder')}
                    disabled={status.submitting}
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="company">{t('contact.form.company')} *</label>
                  <input
                    type="text"
                    id="company"
                    name="company"
                    value={formData.company}
                    onChange={handleChange}
                    placeholder={t('contact.form.companyPlaceholder')}
                    required
                    disabled={status.submitting}
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="subject">{t('contact.form.subject')} *</label>
                  <select
                    id="subject"
                    name="subject"
                    value={formData.subject}
                    onChange={handleChange}
                    required
                    disabled={status.submitting}
                  >
                    <option value="">{t('contact.form.subjectPlaceholder')}</option>
                    <option value="iso-9001">{t('contact.form.subjects.iso9001')}</option>
                    <option value="iso-14001">{t('contact.form.subjects.iso14001')}</option>
                    <option value="iso-22000">{t('contact.form.subjects.iso22000')}</option>
                    <option value="iso-22301">{t('contact.form.subjects.iso22301')}</option>
                    <option value="iso-27001">{t('contact.form.subjects.iso27001')}</option>
                    <option value="iso-37001">{t('contact.form.subjects.iso37001')}</option>
                    <option value="iso-39001">{t('contact.form.subjects.iso39001')}</option>
                    <option value="iso-45001">{t('contact.form.subjects.iso45001')}</option>
                    <option value="iso-50001">{t('contact.form.subjects.iso50001')}</option>
                    <option value="haccp">{t('contact.form.subjects.haccp')}</option>
                    <option value="ce-marking">{t('contact.form.subjects.ceMarking')}</option>
                    <option value="training">{t('contact.form.subjects.training')}</option>
                    <option value="quote">{t('contact.form.subjects.quote')}</option>
                    <option value="other">{t('contact.form.subjects.other')}</option>
                  </select>
                </div>

                <div className="form-group">
                  <label htmlFor="message">{t('contact.form.message')}</label>
                  <textarea
                    id="message"
                    name="message"
                    rows={6}
                    value={formData.message}
                    onChange={handleChange}
                    placeholder={t('contact.form.messagePlaceholder')}
                    disabled={status.submitting}
                  ></textarea>
                </div>

                <div className="form-group form-group-checkbox">
                  <label className="checkbox-label">
                    <input
                      type="checkbox"
                      name="acceptTerms"
                      checked={formData.acceptTerms}
                      onChange={handleChange}
                      required
                      disabled={status.submitting}
                    />
                    <span dangerouslySetInnerHTML={{ __html: t('contact.form.acceptTerms') }} />
                  </label>
                </div>
                <div className="form-group form-group-checkbox">
                  <label className="checkbox-label">
                    <input
                      type="checkbox"
                      name="acceptPrivacy"
                      checked={formData.acceptPrivacy}
                      onChange={handleChange}
                      required
                      disabled={status.submitting}
                    />
                    <span dangerouslySetInnerHTML={{ __html: t('contact.form.acceptPrivacy') }} />
                  </label>
                </div>

                {TURNSTILE_SITE_KEY && (
                  <div className="form-group">
                    <Turnstile siteKey={TURNSTILE_SITE_KEY} onVerify={setTurnstileToken} onExpire={() => setTurnstileToken('')} />
                  </div>
                )}

                <button
                  type="submit"
                  className="btn btn-primary-large"
                  disabled={status.submitting || (!!TURNSTILE_SITE_KEY && !turnstileToken)}
                >
                  {status.submitting ? t('contact.form.submitting') || 'Sending...' : t('contact.form.submit')}
                </button>
              </form>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;
